<template>
  <div class="booking-container">
    <v-layout row wrap>
      <v-flex xs12>
        <v-card flat class="booking-header elevation-2">
          <v-container fluid class="pa-4">
            <h2 class="booking-title earthwerk-font">PAYMENT INFORMATION</h2>
            <div class="title-decoration"></div>
          </v-container>
        </v-card>
      </v-flex>

      <v-flex xs12>
        <v-card flat class="booking-card elevation-2">
          <v-form @submit.prevent="postBooking()"
                  ref="form"
                  v-model="valid"
                  lazy-validation>
            <v-container fluid class="form-grid">
              <!-- Personal Information Section -->
              <v-flex xs12 class="section-header">
                <v-icon color="primary">mdi-account-circle</v-icon>
                <span class="section-title">Personal Information</span>
              </v-flex>

              <v-flex md6 xs12 class="pa-2">
                <v-text-field v-model="booking.first_name"
                              label="First Name"
                              required
                              :rules="requiredRules"
                              outlined
                              dense
                              class="modern-input"
                              prepend-inner-icon="mdi-account"></v-text-field>
              </v-flex>

              <v-flex md6 xs12 class="pa-2">
                <v-text-field v-model="booking.last_name"
                              label="Last Name"
                              required
                              :rules="requiredRules"
                              outlined
                              dense
                              class="modern-input"
                              prepend-inner-icon="mdi-account"></v-text-field>
              </v-flex>

              <v-flex md6 xs12 class="pa-2">
                <v-text-field v-model="booking.email"
                              label="Email"
                              required
                              type="email"
                              :rules="emailRules"
                              outlined
                              dense
                              class="modern-input"
                              prepend-inner-icon="mdi-email"></v-text-field>
              </v-flex>

              <v-flex md6 xs12 class="pa-2">
                <v-autocomplete label="Country"
                                v-model="booking.code"
                                :items="allCountries"
                                :rules="[(v) => !!v || 'Country is required']"
                                required
                                item-text="name"
                                item-value="code"
                                @change="findCountryCode"
                                :loading="$loadingTrue"
                                outlined
                                dense
                                class="modern-input"
                                prepend-inner-icon="mdi-earth"></v-autocomplete>
              </v-flex>

              <v-flex md6 xs12 class="pa-2">
                <v-text-field v-model="booking.phone"
                              label="Phone * (e.g. +XXX...)"
                              required
                              minlength="13"
                              :rules="requiredRules"
                              outlined
                              dense
                              class="modern-input"
                              prepend-inner-icon="mdi-phone"></v-text-field>
              </v-flex>

              <v-flex md6 xs12 class="pa-2">
                <v-text-field v-model="booking.city"
                              label="City"
                              required
                              :rules="requiredRules"
                              outlined
                              dense
                              class="modern-input"
                              prepend-inner-icon="mdi-city"></v-text-field>
              </v-flex>

              <v-flex xs12 class="pa-2">
                <v-text-field v-model="booking.promo_code"
                              label="Promo Code"
                              outlined
                              dense
                              class="modern-input"
                              prepend-inner-icon="mdi-ticket-percent"></v-text-field>
              </v-flex>

              <!-- Entry Information Section -->
              <v-flex xs12 class="section-header">
                <v-icon color="primary">mdi-map-marker</v-icon>
                <span class="section-title">Entry Information</span>
              </v-flex>

              <v-flex md6 xs12 class="pa-2">
                <v-autocomplete label="Mode of Entry"
                                v-model="booking.entry_mode"
                                :items="allEntryModes"
                                item-text="entry_mode_name"
                                item-value="entry_mode_public_id"
                                @change="findEntryPoints"
                                :loading="$loadingTrue"
                                outlined
                                dense
                                class="modern-input"
                                prepend-inner-icon="mdi-vector-polyline"></v-autocomplete>
              </v-flex>

              <v-flex md6 xs12 class="pa-2">
                <v-autocomplete label="Point of Entry"
                                v-model="booking.entry_point"
                                :items="allEntryPoints"
                                item-text="gate_name"
                                item-value="gate_public_id"
                                :loading="$loadingTrue"
                                outlined
                                dense
                                class="modern-input"
                                prepend-inner-icon="mdi-gate"></v-autocomplete>
              </v-flex>

              <v-flex xs12 class="pa-2">
                <v-text-field v-model="booking.mode_registration"
                              label="Mode registration Number"
                              outlined
                              dense
                              class="modern-input"
                              prepend-inner-icon="mdi-numeric"></v-text-field>
              </v-flex>

              <v-flex xs12 class="pa-2">
                <v-textarea v-model="booking.additional_note"
                            label="Additional Note"
                            outlined
                            dense
                            class="modern-input"
                            rows="2"
                            prepend-inner-icon="mdi-note-text"></v-textarea>
              </v-flex>

              <!-- Residency Proof Section -->
              <v-flex xs12 v-if="proofOfResidency.length > 0" class="residency-section pa-2">
                <div class="section-header clickable" @click="residency_proof = true">
                  <v-icon color="primary">mdi-file-document</v-icon>
                  <span class="section-title">Click Here To Proof Residency</span>
                </div>

                <v-dialog v-model="residency_proof" persistent max-width="800px">
                  <v-card class="proof-dialog elevation-4">
                    <v-card-title class="dialog-title">
                      Residency Proof Details
                      <v-spacer></v-spacer>
                      <v-btn icon @click="residency_proof = false">
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </v-card-title>
                    <v-card-text>
                      <div class="proof-grid">
                        <div v-for="(residency, key) in proofOfResidency" :key="key" class="proof-item">
                          <v-text-field readonly
                                        label="Residency"
                                        v-model="proofOfResidency[key].payment_person"
                                        outlined
                                        dense></v-text-field>

                          <v-text-field readonly
                                        label="Point Of Proof"
                                        v-model="proofOfResidency[key].proof_point"
                                        outlined
                                        dense></v-text-field>

                          <v-text-field label="First Name"
                                        v-model="proofOfResidency[key].first_name"
                                        outlined
                                        dense></v-text-field>

                          <v-text-field label="Last Name"
                                        v-model="proofOfResidency[key].last_name"
                                        outlined
                                        dense></v-text-field>

                          <v-text-field label="Document Id"
                                        v-model="proofOfResidency[key].document_id"
                                        outlined
                                        dense></v-text-field>
                        </div>
                      </div>
                    </v-card-text>
                    <v-card-actions class="dialog-actions">
                      <v-btn color="primary"
                             depressed
                             @click="addProof()"
                             :loading="$loadingTrue"
                             class="action-btn">
                        <v-icon left>mdi-check</v-icon>
                        Add Proof
                      </v-btn>
                      <v-btn color="secondary"
                             depressed
                             @click="residency_proof = false"
                             class="action-btn">
                        <v-icon left>mdi-close</v-icon>
                        Cancel
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </v-flex>

              <!-- Donation Section -->
              <v-flex xs12 class="section-header">
                <v-icon color="primary">mdi-heart</v-icon>
                <span class="section-title">DONATE TO THE CONSERVANCY</span>
              </v-flex>

              <v-flex md4 xs12 class="pa-2">
                <v-autocomplete label="Currency"
                                v-model="donation.currency"
                                :items="allCurrencies"
                                @change="changeCurrencyDonation"
                                :return-object="true"
                                item-text="currency_name"
                                item-value="currency_public_id"
                                :loading="$loadingTrue"
                                outlined
                                dense
                                class="modern-input"
                                prepend-inner-icon="mdi-currency-usd"></v-autocomplete>
              </v-flex>

              <v-flex md4 xs12 class="pa-2">
                <v-text-field label="Amount"
                              v-model="donation.amount"
                              type="number"
                              outlined
                              dense
                              class="modern-input"
                              prepend-inner-icon="mdi-cash"></v-text-field>
              </v-flex>

              <v-flex md4 xs12 class="pa-2">
                <v-autocomplete label="Donate to"
                                v-model="donation.donation_to"
                                :items="publicCauses"
                                :return-object="true"
                                @change="changeCauseDonation"
                                item-text="name"
                                item-value="public_id"
                                menu-props="auto"
                                :loading="$loadingTrue"
                                outlined
                                dense
                                class="modern-input"
                                prepend-inner-icon="mdi-charity"></v-autocomplete>
              </v-flex>

              <v-flex xs12 class="pa-2">
                <v-btn depressed
                       color="primary"
                       @click="passAmount"
                       class="donation-btn">
                  <v-icon left>mdi-plus</v-icon>
                  Add Donation
                </v-btn>
              </v-flex>

              <!-- Terms Section -->
              <v-flex xs12 class="terms-section pa-2">
                <div class="terms-item">
                  <router-link target="_blank"
                               :to="{ name: 'PTermsAndConditions' }"
                               class="terms-link">
                    <v-icon small>mdi-file-document</v-icon>
                    Ol Pejeta Conservancy terms and conditions
                  </router-link>
                  <v-checkbox v-model="booking.terms"
                              type="checkbox"
                              label="I agree to the terms and conditions"
                              color="primary"
                              hide-details
                              class="terms-checkbox"></v-checkbox>
                </div>

                <div class="terms-item">
                  <a class="terms-link">
                    <v-icon small>mdi-email-newsletter</v-icon>
                    Receive Emails and Newsletters
                  </a>
                  <v-checkbox v-model="booking.newsletter"
                              type="checkbox"
                              label="I agree to receive promotional emails"
                              color="primary"
                              hide-details
                              class="terms-checkbox"></v-checkbox>
                </div>

                <div class="terms-item">
                  <a class="terms-link" @click="openDisclaimer">
                    <v-icon small>mdi-alert</v-icon>
                    View Liability Disclaimer (Required)
                  </a>
                  <v-checkbox v-model="booking.disclaimer"
                              type="checkbox"
                              label="I accept the liability disclaimer"
                              color="primary"
                              hide-details
                              class="terms-checkbox"
                              :disabled="!disclaimerViewed"></v-checkbox>
                </div>

                <v-dialog v-model="showDisclaimer" persistent max-width="800px">
                  <v-card class="proof-dialog elevation-4">
                    <v-card-title class="dialog-title">
                      Liability Disclaimer
                      <v-spacer></v-spacer>
                      <v-btn icon @click="showDisclaimer = false">
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </v-card-title>
                    <v-card-text class="disclaimer-text">
                      <p class="disclaimer-paragraph"><strong>Please read the following disclaimer carefully before proceeding.</strong></p>
                      <p class="disclaimer-paragraph">By entering the Ol Pejeta Conservancy ("the Conservancy") for any purpose, I acknowledge the inherent risks to my life and property.</p>
                      <p class="disclaimer-paragraph">I confirm that I have read and agree to abide by the Conservancy's "Code of Conduct" to ensure my safety to a reasonable extent.</p>
                      <p class="disclaimer-paragraph">I understand and accept that the Conservancy contains dangerous wild animals, including but not limited to lions, rhinos, elephants, buffaloes, snakes, and insects.</p>
                      <p class="disclaimer-paragraph">By entering the Conservancy, I agree to the following:</p>
                      <ol class="disclaimer-list">
                        <li>Waive and release the Conservancy, Ol Pejeta Conservancy Limited, and its employees, agents, affiliates, and officers from any liability.</li>
                        <li>Hold them harmless from any injury, claim, accident, loss, or damage, including personal injury (fatal or otherwise) and damage to personal property, resulting from my entry.</li>
                        <li>This waiver applies to myself and any minors accompanying me, and is binding on my assigns, heirs, and legal representatives.</li>
                      </ol>
                      <p class="disclaimer-paragraph">If any portion of this waiver is deemed unenforceable, that portion shall be struck out, but the remaining provisions will remain in full force and effect.</p>
                      <p class="disclaimer-paragraph">This waiver is governed by the laws of Kenya.</p>
                    </v-card-text>
                    <v-card-actions class="dialog-actions">
                      <v-btn color="primary"
                             depressed
                             @click="acceptDisclaimer"
                             class="action-btn">
                        <v-icon left>mdi-check</v-icon>
                        Agree
                      </v-btn>
                      <v-btn color="secondary"
                             depressed
                             @click="showDisclaimer = false"
                             class="action-btn">
                        <v-icon left>mdi-close</v-icon>
                        Close
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </v-flex>

              <!-- Submit Button -->
              <v-flex xs12 class="pa-2">
                <v-btn depressed
                       color="primary"
                       block
                       type="submit"
                       :disabled="!booking.terms || !booking.disclaimer"
                       :loading="$loadingTrue"
                       class="submit-btn"
                       large>
                  <v-icon left>mdi-arrow-right</v-icon>
                  Continue to Payment
                </v-btn>
              </v-flex>
            </v-container>
          </v-form>
        </v-card>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
  export default {
    name: "booking-information",
    data() {
      return {
        requiredRules: [(v) => !!v || "This field is required"],
        emailRules: [
          (v) => !!v || "E-mail is required",
          (v) => /.+@.+/.test(v) || "E-mail must be valid",
        ],
        valid: true,
        sheet: false,
        residency_proof: false,
        showDisclaimer: false,
        disclaimerViewed: false,
        booking: {
          first_name: "",
          last_name: "",
          email: "",
          phone: "",
          address: "",
          code: "",
          country: "",
          city: "",
          additional_note: "",
          promo_code: "",
          newsletter: false,
          country_details: {},
          entry_mode: "",
          entry_point: "",
          mode_registration: "",
          terms: false,
          disclaimer: false
        },
        donation: {
          amount: "",
          currency: "",
          donation_to: "",
          cause: "",
          currency_name: "",
        },
        proof: {
          first_name: "",
          last_name: "",
          residency: "",
          document_id: "",
        },
      };
    },
    created() {
      this.$store.dispatch("get_page", "t&c");
      this.$store.dispatch("get_all_countries");
      this.$store.dispatch("get_full_conservation_fees");
      this.$store.dispatch("get_all_causes");
      this.$store.dispatch("get_all_gates");
      this.$store.dispatch("get_all_entry_modes");
      // Automatically open disclaimer dialog on page load to ensure it is viewed
      this.openDisclaimer();
    },
    methods: {
      findCountryCode(data) {
        var instance = this;
        this.allCountries.map(function (country) {
          if (data === country.code) {
            instance.booking.phone = "+" + country.calling_code;
            instance.booking.country_details = country;
          }
        });
      },
      changeCurrencyDonation(name) {
        this.donation.currency = name.currency_public_id;
        this.donation.currency_name = name.currency_name;
      },
      changeCauseDonation(name) {
        this.donation.donation_to = name.public_id;
        this.donation.cause = name.name;
      },
      addProof() {
        this.residency_proof = false;
      },
      passAmount() {
        let donationDetails = [];
        donationDetails.push(this.donation);
        localStorage.setItem("donation", JSON.stringify(donationDetails));
        this.$store.commit("PUBLIC_DONATION", donationDetails);
      },
      postBooking: function () {
        if (this.$refs.form.validate()) {
          var data = {
            guests: this.allConservationFees,
            facilities: this.chosenFacilities,
            inventory: this.chosenInventory,
            vehicles: this.allVehicles,
            donation: this.donation,
            proof_of_residency: this.proofOfResidency,
            external_facilities: this.chosenExternalFacilities,
          };
          var combinedData = Object.assign(
            JSON.parse(localStorage.getItem("user_data")),
            this.booking
          );
          Object.assign(data, combinedData);
          data.currency = this.$store.state.CurrencyStore.browser_currency_id;
          data.currency_id = this.$store.state.CurrencyStore.browser_currency_id;
          localStorage.setItem("previous_booking", JSON.stringify(data));
          this.$store.dispatch("post_booking_public", data);
          this.$store.dispatch("loading_true");
        }
      },
      findEntryPoints: function () {
        this.allEntryPoints;
      },
      openDisclaimer() {
        this.showDisclaimer = true;
        this.disclaimerViewed = true;
      },
      acceptDisclaimer() {
        this.booking.disclaimer = true;
        this.showDisclaimer = false;
      },
    },
    computed: {
      allEntryPoints: function () {
        let allEntry = this.allGates;
        let res = [];
        if (this.booking.entry_mode != '') {
          for (let i = 0; i < allEntry.length; i++) {
            if (allEntry[i].entry_mode_public_id == this.booking.entry_mode) {
              res.push(allEntry[i]);
            }
          }
        }
        return res;
      },
    },
  };
</script>

<style scoped>
  @font-face {
    font-family: 'Earthwerk';
    src: url('PizzaDude.dk-Earthwerk.otf');
    font-weight: normal;
    font-style: normal;
  }

  .booking-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
  }

  .booking-header {
    border-radius: 8px !important;
    margin-bottom: 20px;
    background: linear-gradient(135deg, #74a533 0%, #2E7D32 100%);
    color: white;
  }

  .booking-title {
    color: white;
    font-size: 24px;
    letter-spacing: 1px;
    text-align: center;
    margin-bottom: 8px;
    text-shadow: 1px 1px 1px rgba(0,0,0,0.2);
  }

  .title-decoration {
    width: 60px;
    height: 4px;
    background: rgba(255, 255, 255, 0.7);
    border-radius: 2px;
    margin: 0 auto 8px;
  }

  .booking-card {
    border-radius: 8px !important;
    transition: all 0.3s ease;
    overflow: hidden;
    padding: 20px;
  }

  .form-grid {
    display: flex;
    flex-wrap: wrap;
    margin: -8px;
  }

  .section-header {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 8px;
    margin-bottom: 8px;
    border-bottom: 2px solid #eee;
  }

  .section-title {
    font-weight: 600;
    color: #333;
    font-size: 16px;
  }

  .modern-input {
    margin-bottom: 8px;
  }

    .modern-input > > > .v-input__slot {
      border-radius: 8px !important;
    }

  .residency-section {
    width: 100%;
  }

  .proof-dialog {
    border-radius: 8px !important;
  }

  .dialog-title {
    background: linear-gradient(135deg, #74a533 0%, #2E7D32 100%);
    color: white;
    padding: 16px;
  }

  .proof-grid {
    padding: 16px 0;
  }

  .proof-item {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
    padding: 16px;
    background: #f9f9f9;
    border-radius: 8px;
    margin-bottom: 16px;
  }

  .dialog-actions {
    padding: 16px;
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }

  .action-btn {
    border-radius: 8px !important;
  }

  .terms-section {
    width: 100%;
    background: #f9f9f9;
    padding: 16px;
    border-radius: 8px;
    margin: 16px 0;
  }

  .terms-item {
    margin-bottom: 16px;
  }

  .terms-link {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #74a533;
    text-decoration: none;
    margin-bottom: 8px;
    font-size: 14px;
    cursor: pointer;
  }

    .terms-link:hover {
      text-decoration: underline;
    }

  .terms-checkbox {
    margin-top: 0;
  }

  .donation-btn {
    border-radius: 8px !important;
    background: linear-gradient(135deg, #74a533 0%, #2E7D32 100%);
    color: white !important;
  }

  .submit-btn {
    border-radius: 8px !important;
    margin-top: 16px;
    background: linear-gradient(135deg, #74a533 0%, #2E7D32 100%);
    color: white !important;
  }

  .earthwerk-font {
    font-family: 'Earthwerk', sans-serif;
    color: white;
    line-height: 1.3;
    letter-spacing: 0.5px;
  }

  .disclaimer-text {
    padding: 16px;
    font-size: 14px;
    line-height: 1.6;
    color: #333;
  }

  .disclaimer-paragraph {
    margin-bottom: 12px;
  }

  .disclaimer-list {
    margin-bottom: 12px;
    padding-left: 20px;
  }

    .disclaimer-list li {
      margin-bottom: 8px;
    }

  /* Hover effects */
  .booking-card:hover {
    box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
  }

  /* Responsive adjustments */
  @media (max-width: 960px) {
    .booking-title {
      font-size: 20px;
    }
  }

  @media (max-width: 600px) {
    .booking-container {
      padding: 10px;
    }

    .booking-card {
      padding: 10px;
    }

    .proof-item {
      grid-template-columns: 1fr;
    }

    .disclaimer-text {
      font-size: 13px;
    }
  }
</style>
